import React from "react";

import { PaymentInputsContainer } from 'react-payment-inputs';

import { SvgGuitarPartyLogo } from '../svg';
import { createTemporaryToken, updateCreditcard, fetchTokenStatus } from "../../api/subscription/actions";
import { formatAmount, intervalLabel } from "../subscription/helpers";
import AskellCheckout from "./askell_checkout";

const errorMessages = {
  'Enter a card number': 'Sláið inn kortanúmer',
  'Card number is invalid': 'Kortanúmerið er ógilt',
  'Card must be Visa or Mastercard': 'Kort verður að vera Visa eða Mastercard',
  'Enter an expiry date': 'Sláið inn gildistíma',
  'Expiry month must be between 01 and 12': 'Mánuður í gilditíma skal vera á milli 0-12',
  'Expiry year cannot be in the past': 'Ártal gildistíma má ekki vera liðið',
  'Expiry date cannot be in the past': 'Dagsetning gildistíma má ekki vera liðin',
  'Expiry date is invalid': 'Gildistími er ógildur',
  'Enter a CVC': 'Sláið inn öryggisnúmer',
  'CVC is invalid': 'Öryggisnúmer er ógilt',
}


/*
 * First step of the subscription modal.
 *
 * - New subscribers get Áskell's embedded checkout (Subscription Contracts
 *   V2), see askell_checkout.js.
 * - Existing subscribers use this form to register a new card. The card is
 *   verified through Áskell's 3D Secure flow and attached to the customer,
 *   which is shared by legacy subscriptions and V2 contracts.
 */
class PaymentInformation extends React.Component {
  constructor(props) {
    super(props)

    this.timer = null

    this.state = {
      cardObj: {},
      display: 'cardform'
    }

    this.handleChange = this.handleChange.bind(this)
    this.authorizeCard = this.authorizeCard.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.formDisabled = this.formDisabled.bind(this)

    this.cardNumberValidator = this.cardNumberValidator.bind(this)
  }


  handleChange(e) {
    this.setState({
      cardObj: { ...this.state.cardObj, [e.target.name]: e.target.value }
    })
  }

  onSubmit(e) {
    if (e && e.preventDefault) {
      e.preventDefault()
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.subscription.tokenStatus === 'tokencreated' && (this.props.subscription.tokenStatus !== prevProps.subscription.tokenStatus)) {
      Promise.resolve(this.props.dispatch(updateCreditcard(this.props.user.id, this.props.subscription.tempToken, this.state.cardObj)))
      .then(response => {
        clearInterval(this.timer)
        this.setState({display: 'done'})
      })
    }
    if (this.props.subscription.tokenStatus === 'failed' && (this.props.subscription.tokenStatus !== prevProps.subscription.tokenStatus)) {
      this.setState({display: 'error'})
      clearInterval(this.timer)
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer)
  }

  cardNumberValidator({cardNumber, cardType, errorMessages}){
    if(cardType.displayName === 'Visa' || cardType.displayName === 'Mastercard'){
      return;
    }
    return 'Card must be Visa or Mastercard';
  }

  authorizeCard() {
    // Legacy subscriptions carry their plan id along for the card check;
    // V2 contract holders have no plan, the customer is enough.
    let planId = undefined
    let data = this.props.subscription.data
    if (data && data.plan && data.plan.id) {
      planId = data.plan.id
    }
    Promise.resolve(this.props.dispatch(createTemporaryToken(this.state.cardObj, planId))).then(response => {
      this.setState({display: 'iframe'})
      if ( this.timer === null) {
        this.timer = setInterval(() => this.props.dispatch(fetchTokenStatus(this.props.subscription.tempToken)), 1000)
      }
    })
  }

  formDisabled(meta) {
    return (meta.erroredInputs.cardNumber !== undefined || meta.erroredInputs.cvc !== undefined || meta.erroredInputs.expiryDate !== undefined || !this.state.cardObj.cardholderName || this.props.subscription.isFetching)
  }

  renderCurrentSubscription() {
    let { user } = this.props
    let contract = user.contract && user.contract.is_active ? user.contract : null
    // The legacy plan only when no V2 contract grants access (it may be long expired)
    let legacy = !contract && user.subscription && user.subscription.plan
    if (!contract && !legacy) {
      contract = user.contract
    }

    return (
      <div className="row justify-content-center subscription-interval mb-5">
        <div className="col-md-8 col-16 text-center">
          <p>{gettext('Your current subscription')}:</p>
          {legacy ?
            <>
              <p>{user.subscription.plan.name}</p>
              <p>{formatAmount(user.subscription.plan.amount, user.subscription.plan.currency)}</p>
            </>
            :
            contract &&
            <>
              <p>{contract.product_name}</p>
              <p>{formatAmount(contract.amount, contract.currency)} {intervalLabel(contract.interval, contract.interval_count)}</p>
            </>
          }
          <p>{gettext('Fill in the details below to update your credit card registered with us.')}</p>
        </div>
      </div>
    )
  }

  render() {
    let { user } = this.props

    return (
      <>
        <div className="modal-body payment-info-modal">
          <a className="logo-container" href="/">
            <SvgGuitarPartyLogo />
          </a>
          {this.props.stepIndicator}
          <div className="row justify-content-center mb-5">
            <div className="col-8 text-center">
              <h5 className="modal-title" id="exampleModalLabel">{user.hasSubscription ? gettext('Payment information') : gettext('Buy subscription')}</h5>
            </div>
          </div>

          { !user.hasSubscription &&
            <AskellCheckout dispatch={this.props.dispatch} user={user} source="web-modal" />
          }

          { user.hasSubscription && this.renderCurrentSubscription() }

          { user.hasSubscription && this.state.display === 'cardform' &&
          <PaymentInputsContainer cardNumberValidator={this.cardNumberValidator}>
            {({ meta, getCardImageProps, getCardNumberProps, getExpiryDateProps, getCVCProps }) => {
              return <form onSubmit={this.onSubmit} className="login ">
                <div className="cardholder row justify-content-center">
                  <div className="col-xl-6 col-md-8 col-11">
                    <label htmlFor="cardholder-name">{gettext('Cardholder name')}</label>
                    <input id="cardholder-name" placeholder={gettext('Cardholder name')} className="form-control" onChange={this.handleChange} name="cardholderName" value={this.state.cardObj.cardholderName || ''} />
                    {this.state.cardObj.cardholderName === "" && <div className="error-msg">{gettext('Name can not be empty')}</div>}
                  </div>
                  <div className="col-xl-2 col-md-4 col-5">
                    <label htmlFor="expiry-date">{gettext('Expires')}</label>
                    <input id="expiry-date" placeholder={gettext('Expires')} className="form-control" {...getExpiryDateProps({ onChange: this.handleChange })} value={this.state.cardObj.expiryDate || ''} name="expiryDate" />
                    {meta.erroredInputs.expiryDate !== undefined && <div className="error-msg">{gettext('Four digits')}</div>}
                  </div>
                </div>

                <div className="payment-info row justify-content-center mx-0">
                  <div className="col-xl-6 col-md-8 col-11 cardnr-col">
                    <label htmlFor="creditcard-number" >{gettext('Card number')}</label>
                    <input id="creditcard-number" className="form-control cardnumber" {...getCardNumberProps({ onChange: this.handleChange })} value={this.state.cardObj.cardNumber || ''} name="cardNumber" placeholder={gettext('Card number')} />
                    {meta.error && meta.isTouched && <div className="error-msg">{errorMessages[meta.error] || meta.error}</div>}
                  </div>
                  <div className="col-xl-2 col-md-4 col-5">
                    <label htmlFor="cvc">{gettext('CVC')}</label>
                    <input id="cvc" className="form-control" placeholder={gettext('CVC')} {...getCVCProps({ onChange: this.handleChange, className: "form-control" })} value={this.state.cardObj.cvc || ''} name="cvc" />
                    {meta.erroredInputs.cvc !== undefined && <div className="error-msg">{gettext('Three digits')}</div>}
                  </div>

                  {this.props.subscription.error &&
                    <div className="payment-info row justify-content-center mx-0">
                      <div className="alert alert-danger">{this.props.subscription.error}</div>
                    </div>
                  }
                </div>
                <div className="row justify-content-center">
                  <div className="col-lg-2 col-8 p-0">
                    <button type="button" className="btn btn-primary xs-mt-10 pay" disabled={this.formDisabled(meta)} onClick={this.authorizeCard}>
                      {this.props.subscription.isFetching && <i className="fa fa-circle-o-notch fa-spin fa-fw"></i>}
                      {gettext('Update credit card')}
                    </button>
                  </div>
                </div>

              </form>
            }}
          </PaymentInputsContainer>
          }
          { user.hasSubscription && this.state.display === 'iframe' &&
            <div className="row justify-content-center subscription-iframe-container">
              <div className="col-xl-8 col-md-12 col-16 title">
                <iframe src={this.props.subscription.cardVerificationUrl} />
                {this.props.subscription.error &&
                    <div className="payment-info row justify-content-center mx-0">
                      <div className="alert alert-danger">{this.props.subscription.error}</div>
                    </div>
                  }

              </div>
            </div>
          }
          { user.hasSubscription && this.state.display === 'done' &&
            <div className="row justify-content-center subscription-iframe-container">
              <div className="col-xl-8 col-md-12 col-16 title">
                {gettext('Your credit card has been authenticated and saved.')}
                {this.props.subscription.error &&
                    <div className="payment-info row justify-content-center mx-0">
                      <div className="alert alert-danger">{this.props.subscription.error}</div>
                    </div>
                  }

              </div>
            </div>
          }
          { user.hasSubscription && this.state.display === 'error' &&
            <div className="row justify-content-center subscription-iframe-container">
              <div className="col-xl-8 col-md-12 col-16 title">
                  <div className="payment-info row justify-content-center mx-0">
                    <div className="alert alert-danger">{gettext("We encountered an error during the authentication process. Please try again or contact support if the problem persists.")}</div>
                  </div>
              </div>
            </div>
          }

        </div>
      </>
    )
  }
}

export default PaymentInformation;
